import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, Users, RefreshCw, Eye, ArrowLeft, ArrowRight, Check, AlertTriangle, History, Zap, Trash2, Play, Plus } from "lucide-react";

const PLAN_TIERS = [
  { value: "basic", label: "Creator" },
  { value: "pro", label: "Pro" },
  { value: "pro_unlimited", label: "Pro Unlimited" },
];

const PLAN_STATUSES = [
  { value: "trialing", label: "Trialing" },
  { value: "active", label: "Active (paid)" },
  { value: "canceled", label: "Canceled" },
];

const BRAIN_PROGRESS = [
  { value: "not_started", label: "Never started Dive Brain" },
  { value: "in_progress", label: "Started but not complete" },
  { value: "complete", label: "Completed Dive Brain" },
];

const STRATEGY_PROGRESS = [
  { value: "not_started", label: "No strategy started" },
  { value: "onboarded", label: "Strategy onboarding done" },
  { value: "foundation_approved", label: "Foundation approved" },
];

const TOOL_USAGE = [
  { value: "never", label: "Never used a tool" },
  { value: "low", label: "1–4 tool runs" },
  { value: "active", label: "5+ tool runs" },
];

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold border transition ${
        active
          ? "bg-[#0A2342] text-white border-[#0A2342]"
          : "bg-white text-[#1F2937] border-[#E2E8F0] hover:border-[#0A2342]"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-2">
      <div className="dms-label">{label}</div>
      <div>{children}</div>
      {hint && <div className="text-[11.5px] text-[#94A3B8]">{hint}</div>}
    </div>
  );
}

function toggleValue(arr, val) {
  const s = new Set(arr || []);
  if (s.has(val)) s.delete(val); else s.add(val);
  return Array.from(s);
}

// Preset filters when the funnel widget deep-links here with ?stuck=<stage>.
// Each stage maps to a specific segment + a friendly explanation for the toast.
const STUCK_PRESETS = {
  signed_up_no_brain: {
    filters: { brain_progress: "not_started", plan_statuses: ["trialing"], suspended: false },
    label: "Users who signed up but never started their Dive Brain",
    default_intent: "Nudge users who signed up but haven't started their Dive Brain yet. Remind them the setup takes 3 minutes and they need it to unlock every tool.",
  },
  brain_no_strategy: {
    filters: { brain_progress: "complete", strategy_progress: "not_started", plan_statuses: ["trialing"], suspended: false },
    label: "Users who finished the Dive Brain but haven't started their 90-day Strategy",
    default_intent: "Nudge users who completed their Dive Brain but haven't set up their 90-day Operator yet. Emphasise the delegation angle — their team can execute daily posts once this is done.",
  },
  strategy_no_foundation: {
    filters: { strategy_progress: "onboarded", plan_statuses: ["trialing"], suspended: false },
    label: "Users who finished Strategy onboarding but haven't approved a Foundation",
    default_intent: "Nudge users who finished Strategy onboarding but haven't approved their Foundation yet. Remind them their calendar can't start until they approve — offer to review with them.",
  },
  foundation_no_action: {
    filters: { strategy_progress: "foundation_approved", tool_usage: "never", plan_statuses: ["trialing"], suspended: false },
    label: "Users with an approved Foundation but no posts yet",
    default_intent: "Nudge users with an approved Foundation who haven't marked any post as done. Their calendar is waiting — show them the first entry and ask if they need help.",
  },
  acted_no_convert: {
    filters: { tool_usage: "low", plan_statuses: ["trialing"], suspended: false },
    label: "Trial users who acted at least once but haven't converted",
    default_intent: "Convert trial users who have already shipped at least one post. Show them their consistency streak, tease what happens after day 7, and offer a soft upgrade prompt.",
  },
};

function ScheduleModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    stuck_stage: "brain_no_strategy",
    cron: "weekly_mon",
    hour_utc: 9,
    intent: "",
    tone_hint: "",
    include_incentive: false,
    enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const STAGES = [
    { value: "signed_up_no_brain", label: "Signed up but no Dive Brain" },
    { value: "brain_no_strategy", label: "Brain done but no Strategy" },
    { value: "strategy_no_foundation", label: "Strategy done but no Foundation" },
    { value: "foundation_no_action", label: "Approved Foundation but no post yet" },
    { value: "acted_no_convert", label: "Trial acted but not converted" },
  ];
  const CRONS = [
    { value: "daily", label: "Daily" },
    { value: "weekly_mon", label: "Every Monday" }, { value: "weekly_tue", label: "Every Tuesday" },
    { value: "weekly_wed", label: "Every Wednesday" }, { value: "weekly_thu", label: "Every Thursday" },
    { value: "weekly_fri", label: "Every Friday" }, { value: "weekly_sat", label: "Every Saturday" },
    { value: "weekly_sun", label: "Every Sunday" },
  ];
  const save = async () => {
    if (!form.name.trim() || !form.intent.trim()) { toast.error("Give it a name and a prompt."); return; }
    setSaving(true);
    try {
      await api.post("/admin/broadcast/schedules", form);
      toast.success("Schedule saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading font-bold text-[#0A2342] text-lg">New auto-nudge schedule</h3>
        <Field label="Name"><input data-testid="schedule-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dms-input" placeholder="e.g. Weekly brain-nudge" /></Field>
        <Field label="Target stage">
          <select data-testid="schedule-stage" value={form.stuck_stage} onChange={(e) => setForm({ ...form, stuck_stage: e.target.value })} className="dms-input">
            {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="When">
            <select data-testid="schedule-cron" value={form.cron} onChange={(e) => setForm({ ...form, cron: e.target.value })} className="dms-input">
              {CRONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Hour (UTC)">
            <input data-testid="schedule-hour" type="number" min={0} max={23} value={form.hour_utc} onChange={(e) => setForm({ ...form, hour_utc: parseInt(e.target.value || "9", 10) })} className="dms-input" />
          </Field>
        </div>
        <Field label="What should the AI write?">
          <textarea data-testid="schedule-intent" value={form.intent} onChange={(e) => setForm({ ...form, intent: e.target.value })} rows={4} className="dms-input font-mono text-[13px]" placeholder="e.g. Nudge users who finished the Dive Brain but haven't started their 90-day Operator. Emphasise team-execution." />
        </Field>
        <label className="flex items-center gap-2 text-[13px]"><input data-testid="schedule-incentive" type="checkbox" checked={form.include_incentive} onChange={(e) => setForm({ ...form, include_incentive: e.target.checked })} /> Include an incentive (bonus credits / call)</label>
        <label className="flex items-center gap-2 text-[13px]"><input data-testid="schedule-enabled" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled</label>
        <div className="flex gap-2 pt-2">
          <button data-testid="schedule-cancel" onClick={onClose} className="dms-btn-secondary flex-1 justify-center">Cancel</button>
          <button data-testid="schedule-save" onClick={save} disabled={saving} className="dms-btn-primary flex-1 justify-center disabled:opacity-40">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBroadcast() {
  const [step, setStep] = useState(1);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [tagBuckets, setTagBuckets] = useState({});
  const [filters, setFilters] = useState({
    plan_tiers: [],
    plan_statuses: [],
    brain_progress: null,
    strategy_progress: null,
    tool_usage: null,
    business_types: [],
    tags_any: [],
    email_verified: null,
    days_since_signup_max: null,
  });
  const [preview, setPreview] = useState({ count: null, sample: [], over_cap: false, loading: false });
  const [intent, setIntent] = useState("");
  const [toneHint, setToneHint] = useState("");
  const [incentive, setIncentive] = useState(false);
  const [draft, setDraft] = useState({ subject: "", preview_text: "", body_markdown: "", body_html: "" });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const loadSchedules = async () => {
    try {
      const { data } = await api.get("/admin/broadcast/schedules");
      setSchedules(data.items || []);
    } catch (e) { console.debug("schedules load failed:", e?.response?.status); }
  };

  useEffect(() => {
    api.get("/admin/broadcast/facets").then(({ data }) => {
      setBusinessTypes(data.business_types || []);
      setTagBuckets(data.tag_buckets || {});
    }).catch(() => {});
    api.get("/admin/broadcast/history?limit=10").then(({ data }) => setHistory(data.items || [])).catch(() => {});
    loadSchedules();
  }, []);

  // Handle deep-link from the funnel widget: /admin/broadcast?stuck=<stage>
  const [params] = useSearchParams();
  useEffect(() => {
    const stuck = params.get("stuck");
    if (!stuck || !STUCK_PRESETS[stuck]) return;
    const preset = STUCK_PRESETS[stuck];
    setFilters((prev) => ({
      ...prev,
      plan_tiers: [], plan_statuses: [], brain_progress: null, strategy_progress: null,
      tool_usage: null, business_types: [], tags_any: [], email_verified: null, days_since_signup_max: null,
      ...preset.filters,
    }));
    if (preset.default_intent && !intent) setIntent(preset.default_intent);
    setStep(1);
    toast.success(`Prefilled for: ${preset.label}`);
  }, [params.get("stuck")]);

  const cleanFilters = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      if (Array.isArray(v) && v.length === 0) return;
      out[k] = v;
    });
    return out;
  }, [filters]);

  const runPreview = async () => {
    setPreview((p) => ({ ...p, loading: true }));
    try {
      const { data } = await api.post("/admin/broadcast/segment/preview", { filters: cleanFilters });
      setPreview({ ...data, loading: false });
    } catch (err) {
      setPreview({ count: null, sample: [], over_cap: false, loading: false });
      toast.error(err?.response?.data?.detail || "Preview failed");
    }
  };

  useEffect(() => { runPreview(); }, [JSON.stringify(cleanFilters)]);

  const runDraft = async () => {
    if (intent.trim().length < 10) {
      toast.error("Tell the AI a bit more about what to write (min 10 chars).");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/broadcast/draft", {
        intent, filters: cleanFilters, tone_hint: toneHint || null, include_incentive: incentive,
      });
      setDraft({ subject: data.subject, preview_text: data.preview_text, body_markdown: data.body_markdown, body_html: data.body_html });
      toast.success("Draft ready — edit as you like");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/admin/broadcast/send-test", {
        subject: draft.subject, body_markdown: draft.body_markdown, preview_text: draft.preview_text,
      });
      toast.success(`Test sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    }
  };

  const sendReal = async () => {
    if (!window.confirm(`Send this email to ${preview.count > 500 ? 500 : preview.count} users? This cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/send", {
        subject: draft.subject, body_markdown: draft.body_markdown, preview_text: draft.preview_text,
        filters: cleanFilters, intent,
      });
      const overCapMsg = data.capped ? ` (capped from ${data.matched_count})` : "";
      toast.success(`Broadcast queued — sending to ${data.recipient_count} users${overCapMsg}. Rates will appear as recipients open.`);
      setStep(1);
      setIntent(""); setDraft({ subject: "", preview_text: "", body_markdown: "", body_html: "" });
      // reload history — small delay so the initial 'in_progress' row is fetched
      setTimeout(async () => {
        try {
          const { data: h } = await api.get("/admin/broadcast/history?limit=10");
          setHistory(h.items || []);
        } catch (e) { console.debug("history refresh failed:", e); }
      }, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const canDraft = intent.trim().length >= 10;
  const canSend = draft.subject && draft.body_html && (preview.count ?? 0) > 0;

  return (
    <div data-testid="admin-broadcast" className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-[#0A2342]">Broadcast email</h1>
        <p className="text-[#64748B] text-[13.5px] mt-1">Draft an on-brand email with AI, target a specific segment, and send through Brevo — all without leaving DMS.</p>
      </div>

      {/* stepper */}
      <div className="flex items-center gap-3 text-[12px] font-semibold">
        {[
          { n: 1, label: "Audience", icon: Users },
          { n: 2, label: "Draft", icon: Sparkles },
          { n: 3, label: "Review & send", icon: Send },
        ].map(({ n, label, icon: Icon }) => (
          <div key={n} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${step === n ? "bg-[#0A2342] text-white border-[#0A2342]" : "bg-white text-[#64748B] border-[#E2E8F0]"}`}>
            <Icon className="w-3.5 h-3.5" /> {n}. {label}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 dms-card p-5 space-y-5">
            <Field label="Plan tier"><div className="flex flex-wrap gap-2">
              {PLAN_TIERS.map((t) => (
                <Chip key={t.value} testid={`filter-tier-${t.value}`} active={filters.plan_tiers.includes(t.value)} onClick={() => setFilters({ ...filters, plan_tiers: toggleValue(filters.plan_tiers, t.value) })}>{t.label}</Chip>
              ))}
            </div></Field>

            <Field label="Plan status"><div className="flex flex-wrap gap-2">
              {PLAN_STATUSES.map((t) => (
                <Chip key={t.value} testid={`filter-status-${t.value}`} active={filters.plan_statuses.includes(t.value)} onClick={() => setFilters({ ...filters, plan_statuses: toggleValue(filters.plan_statuses, t.value) })}>{t.label}</Chip>
              ))}
            </div></Field>

            <Field label="Dive Brain progress"><div className="flex flex-wrap gap-2">
              {BRAIN_PROGRESS.map((t) => (
                <Chip key={t.value} testid={`filter-brain-${t.value}`} active={filters.brain_progress === t.value} onClick={() => setFilters({ ...filters, brain_progress: filters.brain_progress === t.value ? null : t.value })}>{t.label}</Chip>
              ))}
            </div></Field>

            <Field label="Strategy progress"><div className="flex flex-wrap gap-2">
              {STRATEGY_PROGRESS.map((t) => (
                <Chip key={t.value} testid={`filter-strategy-${t.value}`} active={filters.strategy_progress === t.value} onClick={() => setFilters({ ...filters, strategy_progress: filters.strategy_progress === t.value ? null : t.value })}>{t.label}</Chip>
              ))}
            </div></Field>

            <Field label="Tool usage"><div className="flex flex-wrap gap-2">
              {TOOL_USAGE.map((t) => (
                <Chip key={t.value} testid={`filter-usage-${t.value}`} active={filters.tool_usage === t.value} onClick={() => setFilters({ ...filters, tool_usage: filters.tool_usage === t.value ? null : t.value })}>{t.label}</Chip>
              ))}
            </div></Field>

            {businessTypes.length > 0 && (
              <Field label="Business type (from Dive Brain answers)"><div className="flex flex-wrap gap-2">
                {businessTypes.map((t) => (
                  <Chip key={t} testid={`filter-business-${t}`} active={filters.business_types.includes(t)} onClick={() => setFilters({ ...filters, business_types: toggleValue(filters.business_types, t) })}>{t}</Chip>
                ))}
              </div></Field>
            )}

            {Object.keys(tagBuckets).length > 0 && (
              <Field label="Tags (match ANY selected)" hint="Auto-generated from user profile — tier, status, identity, goals.">
                <div className="space-y-2.5">
                  {Object.entries(tagBuckets).map(([bucket, tags]) => (
                    <div key={bucket}>
                      <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#94A3B8] mb-1.5">{bucket}</div>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((t) => (
                          <Chip
                            key={t}
                            testid={`filter-tag-${t.replace(/[^a-z0-9]+/gi, "-")}`}
                            active={filters.tags_any.includes(t)}
                            onClick={() => setFilters({ ...filters, tags_any: toggleValue(filters.tags_any, t) })}
                          >{t}</Chip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Field>
            )}

            <Field label="Only email-verified"><div className="flex gap-2">
              <Chip testid="filter-verified-yes" active={filters.email_verified === true} onClick={() => setFilters({ ...filters, email_verified: filters.email_verified === true ? null : true })}>Verified only</Chip>
              <Chip testid="filter-verified-no" active={filters.email_verified === false} onClick={() => setFilters({ ...filters, email_verified: filters.email_verified === false ? null : false })}>Unverified only</Chip>
            </div></Field>

            <Field label="Only signed up within" hint="Leave blank for all">
              <div className="flex flex-wrap gap-2">
                {[7, 14, 30, 60, 90].map((d) => (
                  <Chip key={d} testid={`filter-days-${d}`} active={filters.days_since_signup_max === d} onClick={() => setFilters({ ...filters, days_since_signup_max: filters.days_since_signup_max === d ? null : d })}>Last {d} days</Chip>
                ))}
              </div>
            </Field>
          </div>

          <div className="lg:col-span-1 space-y-4">
            <div data-testid="segment-preview" className="dms-card p-5 sticky top-24">
              <div className="dms-label mb-2">Segment preview</div>
              <div className="flex items-center gap-2">
                {preview.loading
                  ? <><Loader2 className="w-4 h-4 animate-spin text-[#64748B]" /><span className="text-[13.5px] text-[#64748B]">Computing…</span></>
                  : <div className="text-[28px] font-heading font-bold text-[#0A2342]">{preview.count ?? "—"}<span className="text-[13px] font-normal text-[#64748B] ml-1.5">recipients</span></div>}
              </div>
              {preview.over_cap && (
                <div className="mt-3 text-[12px] text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-2.5 py-1.5 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Only the first {preview.capped_at} users will receive the broadcast (per-batch cap).</span>
                </div>
              )}
              <div className="mt-3 space-y-2 max-h-64 overflow-auto">
                {(preview.sample || []).map((r) => (
                  <div key={r.user_id} className="text-[11.5px] text-[#475569] leading-tight border-t border-[#F1F5F9] pt-1.5">
                    <div className="font-semibold text-[#0A2342]">{r.name || "(no name)"}</div>
                    <div>{r.email}</div>
                    <div className="text-[10.5px] text-[#94A3B8] mt-0.5">
                      {r.plan_tier || "trial"} · {r.brain_complete ? "brain ✓" : (r.brain_identity ? "brain partial" : "no brain")} · {r.artifact_count} artifacts
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid="btn-refresh-preview" onClick={runPreview} className="mt-4 text-[12px] font-semibold text-[#1D4E89] inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
              <button
                data-testid="btn-next-draft"
                disabled={!(preview.count > 0)}
                onClick={() => setStep(2)}
                className="mt-4 w-full dms-btn-primary justify-center disabled:opacity-40"
              >Next: draft the email <ArrowRight className="w-4 h-4" /></button>
            </div>

            {history.length > 0 && (
              <div className="dms-card p-4">
                <div className="dms-label mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Recent broadcasts</div>
                <div className="space-y-3">
                  {history.map((h) => (
                    <div key={h.broadcast_id} data-testid={`history-${h.broadcast_id}`} className="text-[11.5px] border-t border-[#F1F5F9] pt-2">
                      <div className="font-semibold text-[#0A2342] truncate">{h.subject}</div>
                      <div className="text-[#94A3B8] mt-0.5">{(h.sent_at || "").slice(0, 10)} · {h.sent_count ?? h.recipient_count} sent</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10.5px] font-semibold">
                        <span className="px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">Opens {h.open_rate ?? 0}%</span>
                        <span className="px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">Clicks {h.click_rate ?? 0}%</span>
                        {(h.bounces_count ?? 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C]">Bounces {h.bounces_count}</span>}
                        {(h.unsub_count ?? 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">Unsub {h.unsub_count}</span>}
                        {h.auto_schedule_id && <span className="px-1.5 py-0.5 rounded bg-[#DBEAFE] text-[#1D4E89] inline-flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" /> auto</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="dms-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="dms-label flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Auto-nudge schedules</div>
                <button data-testid="add-schedule-btn" onClick={() => setShowScheduleModal(true)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1D4E89] hover:text-[#0A2342]"><Plus className="w-3 h-3" /> Add</button>
              </div>
              {schedules.length === 0 ? (
                <div className="text-[11.5px] text-[#94A3B8] py-2">Set up an automation that runs weekly to nudge stuck users.</div>
              ) : (
                <div className="space-y-2">
                  {schedules.map((s) => (
                    <div key={s.schedule_id} data-testid={`schedule-${s.schedule_id}`} className="text-[11.5px] border-t border-[#F1F5F9] pt-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-[#0A2342] truncate">{s.name}</div>
                          <div className="text-[#94A3B8] mt-0.5">{s.stuck_stage} · {s.cron.replace("_", " ")} @ {s.hour_utc}:00 UTC · {s.enabled ? "on" : "off"}</div>
                          {s.last_run_at && <div className="text-[10.5px] text-[#64748B] mt-0.5">Last: {String(s.last_run_at).slice(0, 10)} → {s.last_result?.sent ?? 0} sent{s.last_error ? ` · ${s.last_error}` : ""}</div>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button title="Run now" data-testid={`schedule-run-${s.schedule_id}`} onClick={async () => { try { const { data } = await api.post(`/admin/broadcast/schedules/${s.schedule_id}/run-now`); toast.success(`Ran: ${data.summary.sent ?? 0} sent, ${data.summary.failed ?? 0} failed`); loadSchedules(); } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); } }} className="p-1 rounded hover:bg-[#F1F5F9]"><Play className="w-3 h-3 text-[#0F766E]" /></button>
                          <button title="Delete" data-testid={`schedule-delete-${s.schedule_id}`} onClick={async () => { if (!window.confirm(`Delete schedule "${s.name}"?`)) return; await api.delete(`/admin/broadcast/schedules/${s.schedule_id}`); toast.success("Deleted"); loadSchedules(); }} className="p-1 rounded hover:bg-[#F1F5F9]"><Trash2 className="w-3 h-3 text-[#B91C1C]" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {showScheduleModal && <ScheduleModal onClose={() => setShowScheduleModal(false)} onSaved={() => { setShowScheduleModal(false); loadSchedules(); }} />}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="dms-card p-5 space-y-4">
            <Field label="Tell the AI what to write" hint="Be direct — the more specific you are, the better the draft.">
              <textarea data-testid="broadcast-intent" value={intent} onChange={(e) => setIntent(e.target.value)} rows={5} className="dms-input font-mono text-[13px]" placeholder='e.g. "Nudge users who started the Dive Brain but did not finish — remind them the strategy generator is free during trial and offer 25 bonus credits if they finish this week."' />
            </Field>
            <Field label="Tone (optional)">
              <input data-testid="broadcast-tone" value={toneHint} onChange={(e) => setToneHint(e.target.value)} className="dms-input" placeholder="warm, direct, encouraging (default)" />
            </Field>
            <label className="flex items-center gap-2 text-[13px] text-[#1F2937] select-none cursor-pointer">
              <input data-testid="broadcast-incentive" type="checkbox" checked={incentive} onChange={(e) => setIncentive(e.target.checked)} className="h-4 w-4 rounded border-[#CBD5E1]" />
              This email offers an incentive (credits / bonus / call)
            </label>
            <div className="flex gap-2 pt-2">
              <button data-testid="btn-back-audience" onClick={() => setStep(1)} className="dms-btn-secondary"><ArrowLeft className="w-4 h-4" /> Audience</button>
              <button data-testid="btn-generate-draft" onClick={runDraft} disabled={!canDraft || drafting} className="dms-btn-primary flex-1 justify-center disabled:opacity-40">
                {drafting ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4" /> {draft.subject ? "Regenerate" : "Generate draft"}</>}
              </button>
            </div>
          </div>

          <div className="dms-card p-5 space-y-3">
            <Field label="Subject (editable)"><input data-testid="draft-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="dms-input" placeholder="AI-generated subject appears here" /></Field>
            <Field label="Preview line"><input data-testid="draft-preview" value={draft.preview_text} onChange={(e) => setDraft({ ...draft, preview_text: e.target.value })} className="dms-input" /></Field>
            <Field label="Body (markdown — editable)" hint="Use {first_name} and {cta_url} for per-recipient personalisation.">
              <textarea
                data-testid="draft-body"
                value={draft.body_markdown}
                onChange={(e) => {
                  const md = e.target.value;
                  // Preview parser — mirrors server broadcast_service.markdown_to_html:
                  // paragraphs (blank-line separated), links [text](url), **bold**, *italic*.
                  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
                  const inline = (t) => {
                    let out = esc(t);
                    out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g,
                      (_, txt, url) => `<a href="${url}" style="color:#1D4E89;text-decoration:underline">${txt}</a>`);
                    out = out.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
                    out = out.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
                    return out;
                  };
                  const html = md.split(/\n{2,}/).map((p) => `<p style="margin:0 0 14px 0;line-height:1.6">${inline(p.trim())}</p>`).join("");
                  setDraft({ ...draft, body_markdown: md, body_html: html });
                }}
                rows={12} className="dms-input font-mono text-[13px]"
              />
            </Field>
            <button
              data-testid="btn-next-review"
              onClick={() => setStep(3)} disabled={!draft.subject || !draft.body_html}
              className="w-full dms-btn-primary justify-center disabled:opacity-40"
            >Next: review & send <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 dms-card p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#94A3B8]">Subject</div>
              <div className="font-semibold text-[#0A2342] mt-0.5">{draft.subject}</div>
              {draft.preview_text && <div className="text-[12.5px] text-[#64748B] mt-1">{draft.preview_text}</div>}
            </div>
            <div className="p-5 max-h-[500px] overflow-auto" data-testid="draft-preview-html">
              <iframe title="preview" sandbox="allow-same-origin" className="w-full h-[460px] border border-[#E2E8F0] rounded-md bg-white" srcDoc={draft.body_html.replace(/\{first_name\}/g, "Robin").replace(/\{cta_url\}/g, "#")} />
            </div>
          </div>
          <div className="dms-card p-5 space-y-3 sticky top-24 self-start">
            <div className="dms-label">Ready to send</div>
            <div className="text-[24px] font-heading font-bold text-[#0A2342]">{preview.count}<span className="text-[13px] font-normal text-[#64748B] ml-1.5">recipients</span></div>
            <button data-testid="btn-send-test" onClick={sendTest} className="w-full dms-btn-secondary justify-center"><Eye className="w-4 h-4" /> Send test to me</button>
            <button data-testid="btn-send-real" onClick={sendReal} disabled={!canSend || sending} className="w-full dms-btn-primary justify-center disabled:opacity-40">
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send to {preview.count} users</>}
            </button>
            <div className="text-[11.5px] text-[#94A3B8] leading-relaxed">
              <Check className="inline w-3 h-3 text-[#0F766E]" /> Unsubscribe footer added automatically.<br />
              <Check className="inline w-3 h-3 text-[#0F766E]" /> Prior unsubscribers are excluded.<br />
              <Check className="inline w-3 h-3 text-[#0F766E]" /> This send will be logged in history.
            </div>
            <button data-testid="btn-back-draft" onClick={() => setStep(2)} className="w-full text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center justify-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back to draft</button>
          </div>
        </div>
      )}
    </div>
  );
}
